/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useRef } from 'react';
import { useWBL } from '../../context/WBLContext';
import { 
  Download, 
  FileText, 
  Filter, 
  Calendar, 
  Users, 
  Briefcase, 
  Activity, 
  BarChart3, 
  ChevronDown, 
  CheckCircle, 
  Globe, 
  GraduationCap, 
  TrendingUp, 
  DollarSign,
  Building2,
  ShieldCheck,
  Award,
  CheckCircle2,
  Clock,
  ExternalLink
} from 'lucide-react';
import { HeroBanner } from '../Common/HeroBanner';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line } from 'recharts';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const ReportsPage: React.FC = () => {
  const {
    tenants,
    users,
    opportunities,
    placements,
    applications,
    interviews,
    allSchools,
    activeUser,
    activeTenant,
    systemConfig,
    selectedTerm,
    setSelectedTerm
  } = useWBL();

  const [activeTab, setActiveTab] = useState<string>('overview');
  const [activePerspective, setActivePerspective] = useState<string>('global');
  const [selectedAdvisorSchool, setSelectedAdvisorSchool] = useState<string>('all');
  const [exportLoading, setExportLoading] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const isAdvisor = activeUser?.role === 'advisor';
  const isOrg = activeUser?.role === 'organization';
  const isSuperadmin = activeUser?.role === 'superadmin';
  const isRegionalAdmin = activeUser?.role === 'regional_admin';

  const canAccessReports = isSuperadmin || isRegionalAdmin || isOrg || isAdvisor;

  const orgDisplayName = activeUser?.orgName || activeUser?.name || 'Organization';
  const normOrgName = (activeUser?.orgName || activeUser?.name || '').toLowerCase().trim();
  const orgUid = activeUser?.uid;

  // All opportunity IDs belonging to this organization (across terms, for linking apps/placements/interviews)
  const allOrgOppIds = useMemo(() => {
    if (!isOrg) return new Set<string>();
    return new Set(
      (opportunities || [])
        .filter(o => {
          if (orgUid && (o.organizationId === orgUid || (o as any).createdBy === orgUid)) return true;
          const oName = (o.organizationName || (o as any).orgName || '').toLowerCase().trim();
          return Boolean(normOrgName && oName === normOrgName);
        })
        .map(o => o.id)
    );
  }, [opportunities, isOrg, orgUid, normOrgName]);

  // Resolve schools associated with this Advisor
  const advisorSchools = useMemo(() => {
    if (!activeUser || !isAdvisor) return [];
    const allRefs = Array.from(new Set([
      activeUser.schoolId,
      ...(activeUser.schoolsServedIds || []),
      activeUser.school,
      ...(activeUser.schoolsServed || []),
      activeUser.pendingSchoolId,
      activeUser.pendingSchool
    ].filter(Boolean) as string[]));

    return (allSchools || []).filter(s => {
      return allRefs.some(ref => {
        const normRef = ref.toLowerCase().trim().replace(/\s*\(.*\)$/, '');
        const normSchoolName = s.name.toLowerCase().trim().replace(/\s*\(.*\)$/, '');
        const normSchoolId = s.id.toLowerCase().trim().replace(/\s*\(.*\)$/, '');
        return normRef === normSchoolId || normRef === normSchoolName;
      });
    });
  }, [allSchools, activeUser, isAdvisor]);

  const advisorSchoolIds = useMemo(() => advisorSchools.map(s => s.id), [advisorSchools]);
  const advisorSchoolNames = useMemo(() => advisorSchools.map(s => s.name.toLowerCase().trim()), [advisorSchools]);

  // Advisor's designated students (FERPA-verified & assigned to advisor's schools)
  const designatedStudents = useMemo(() => {
    if (!activeUser || !isAdvisor) return [];
    if (!activeUser.verified) return [];
    return (users || []).filter(u => {
      if (u.role !== 'student') return false;
      
      // Must have FERPA consent granted
      const ferpaGranted = typeof u.ferpaConsent === 'object'
        ? u.ferpaConsent?.granted === true
        : u.ferpaConsent === true;
      if (!ferpaGranted) return false;

      // School match
      if (u.schoolId && advisorSchoolIds.includes(u.schoolId)) return true;
      if (u.school && advisorSchoolNames.includes(u.school.toLowerCase().trim())) return true;
      if (u.additionalSchoolIds && u.additionalSchoolIds.some((id: string) => advisorSchoolIds.includes(id))) return true;
      if (u.additionalSchools && u.additionalSchools.some((sch: string) => advisorSchoolNames.includes(sch.toLowerCase().trim()))) return true;
      return false;
    });
  }, [users, advisorSchoolIds, advisorSchoolNames, activeUser, isAdvisor]);

  // Scoped student IDs for Advisor
  const scopedAdvisorStudents = useMemo(() => {
    if (!isAdvisor) return [];
    if (selectedAdvisorSchool === 'all') return designatedStudents;
    return designatedStudents.filter(s => 
      s.schoolId === selectedAdvisorSchool || 
      s.school === selectedAdvisorSchool || 
      s.additionalSchoolIds?.includes(selectedAdvisorSchool)
    );
  }, [isAdvisor, selectedAdvisorSchool, designatedStudents]);

  const advisorStudentUidSet = useMemo(() => new Set(scopedAdvisorStudents.map(s => s.uid)), [scopedAdvisorStudents]);

  // Sync perspective for regional_admin and superadmin
  React.useEffect(() => {
    if (isRegionalAdmin) {
      const regId = (activeTenant?.id && activeTenant.id !== 'global') ? activeTenant.id : (activeUser?.tenantId || 'global');
      if (activePerspective !== regId) {
        setActivePerspective(regId);
      }
    } else if (isSuperadmin && activeTenant?.id && activeTenant.id !== 'global' && activePerspective === 'global') {
      setActivePerspective(activeTenant.id);
    }
  }, [activeUser, activeTenant?.id, isRegionalAdmin, isSuperadmin]);

  // Effective region ID for regional_admin and superadmin
  const effectiveRegionId = useMemo(() => {
    if (isRegionalAdmin) {
      return (activeTenant?.id && activeTenant.id !== 'global') ? activeTenant.id : (activeUser?.tenantId || 'global');
    }
    if (isSuperadmin) {
      return activePerspective;
    }
    return activeTenant?.id || activeUser?.tenantId || 'global';
  }, [isRegionalAdmin, isSuperadmin, activeTenant?.id, activeUser?.tenantId, activePerspective]);

  // ONLY superadmin can have global cross-region view; regional_admin has full region view for their region
  const isGlobal = isSuperadmin && effectiveRegionId === 'global';

  // Tabs configured by role
  const tabs = useMemo(() => {
    if (isAdvisor) {
      return [
        { id: 'overview', label: 'Cohort Overview', icon: BarChart3 },
        { id: 'placements', label: 'Advisee Placements', icon: CheckCircle },
        { id: 'opportunities', label: 'Host Opportunities', icon: Briefcase },
        { id: 'impact', label: 'Economic Impact', icon: TrendingUp },
        { id: 'regional', label: 'Assigned Schools', icon: Building2 },
        { id: 'system', label: 'Cohort Readiness', icon: Activity },
      ];
    }
    if (isOrg) {
      return [
        { id: 'overview', label: 'Org Overview', icon: BarChart3 },
        { id: 'placements', label: 'My Placements', icon: CheckCircle },
        { id: 'opportunities', label: 'My Opportunities', icon: Briefcase },
        { id: 'impact', label: 'Economic Impact', icon: TrendingUp },
        { id: 'regional', label: 'Partner Schools', icon: GraduationCap },
        { id: 'system', label: 'Pipeline Readiness', icon: Activity },
        { id: 'regional_health', label: 'Regional Health', icon: Globe },
      ];
    }
    return [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'placements', label: 'Placements', icon: CheckCircle },
      { id: 'opportunities', label: 'Opportunities', icon: Briefcase },
      { id: 'impact', label: 'Economic Impact', icon: TrendingUp },
      { id: 'regional', label: 'Regional', icon: Globe },
      { id: 'system', label: 'System Health', icon: Activity },
    ];
  }, [isAdvisor, isOrg]);

  // Filtered Applications (STRICTLY scoped by role)
  const filteredApps = useMemo(() => {
    let apps = applications || [];

    if (isAdvisor) {
      // ONLY data from advisor's students
      apps = apps.filter(a => advisorStudentUidSet.has(a.studentId));
    } else if (isOrg) {
      // ONLY applications to THIS organization's opportunities (organizations transcend term)
      return apps.filter(a => {
        if (orgUid && (a as any).organizationId === orgUid) return true;
        if (allOrgOppIds.has(a.opportunityId)) return true;
        const aOrgName = (a.organizationName || (a as any).orgName || '').toLowerCase().trim();
        return Boolean(normOrgName && aOrgName === normOrgName);
      });
    } else if (!isGlobal) {
      // Regional Admin or Superadmin viewing a specific region
      apps = apps.filter(a => a.tenantId === effectiveRegionId);
    }

    return apps.filter(a => {
      if (selectedTerm === 'All Terms') return true;
      const opp = (opportunities || []).find(o => o.id === a.opportunityId);
      const termMatch = opp
        ? (opp.semester === selectedTerm || opp.term === selectedTerm || (!opp.semester && !opp.term))
        : (a.term === selectedTerm || !a.term);
      return termMatch;
    });
  }, [applications, isAdvisor, advisorStudentUidSet, isOrg, orgUid, allOrgOppIds, normOrgName, isGlobal, effectiveRegionId, selectedTerm, opportunities]);

  // Filtered Placements (STRICTLY scoped by role)
  const filteredPlacements = useMemo(() => {
    let pms = placements || [];

    if (isAdvisor) {
      // ONLY placements for advisor's students
      pms = pms.filter(p => advisorStudentUidSet.has(p.studentId));
    } else if (isOrg) {
      // ONLY placements at THIS organization (organizations transcend term)
      return pms.filter(p => {
        if (orgUid && p.organizationId === orgUid) return true;
        if (allOrgOppIds.has(p.opportunityId)) return true;
        const pOrgName = (p.organizationName || (p as any).orgName || '').toLowerCase().trim();
        return Boolean(normOrgName && pOrgName === normOrgName);
      });
    } else if (!isGlobal) {
      // Regional Admin or Superadmin viewing a specific region
      pms = pms.filter(p => p.tenantId === effectiveRegionId);
    }

    return pms.filter(p => selectedTerm === 'All Terms' || p.term === selectedTerm || !p.term);
  }, [placements, isAdvisor, advisorStudentUidSet, isOrg, orgUid, allOrgOppIds, normOrgName, isGlobal, effectiveRegionId, selectedTerm]);

  // Filtered Opportunities (STRICTLY scoped by role)
  const filteredOpps = useMemo(() => {
    let opps = opportunities || [];

    if (isAdvisor) {
      const studentAppliedOppIds = new Set(filteredApps.map(a => a.opportunityId));
      opps = opps.filter(o => {
        const isApplied = studentAppliedOppIds.has(o.id);
        const inRegion = o.tenantId === activeUser?.tenantId || o.tenantId === 'global' || !o.tenantId;
        return isApplied || inRegion;
      });
    } else if (isOrg) {
      // ONLY opportunities created by / belonging to THIS organization (organizations transcend term)
      return opps.filter(o => {
        if (orgUid && (o.organizationId === orgUid || (o as any).createdBy === orgUid)) return true;
        const oName = (o.organizationName || (o as any).orgName || '').toLowerCase().trim();
        return Boolean(normOrgName && oName === normOrgName);
      });
    } else if (!isGlobal) {
      // Regional Admin or Superadmin viewing a specific region
      opps = opps.filter(o => o.tenantId === effectiveRegionId);
    }

    return opps.filter(o => selectedTerm === 'All Terms' || o.semester === selectedTerm || o.term === selectedTerm || (!o.semester && !o.term));
  }, [opportunities, isAdvisor, filteredApps, isOrg, orgUid, normOrgName, isGlobal, effectiveRegionId, selectedTerm, activeUser?.tenantId]);

  // Scoped Interviews (STRICTLY scoped by role)
  const filteredInterviews = useMemo(() => {
    let ivs = interviews || [];
    if (isAdvisor) {
      ivs = ivs.filter(i => advisorStudentUidSet.has(i.studentId));
    } else if (isOrg) {
      const orgAppIds = new Set(filteredApps.map(a => a.id));
      return ivs.filter(i => {
        if (orgUid && (i as any).organizationId === orgUid) return true;
        if (i.opportunityId && allOrgOppIds.has(i.opportunityId)) return true;
        if (i.applicationId && orgAppIds.has(i.applicationId)) return true;
        const iOrgName = (i.organizationName || (i as any).orgName || '').toLowerCase().trim();
        return Boolean(normOrgName && iOrgName === normOrgName);
      });
    } else if (!isGlobal) {
      ivs = ivs.filter(i => i.tenantId === effectiveRegionId);
    }
    return ivs;
  }, [interviews, isAdvisor, advisorStudentUidSet, isOrg, orgUid, allOrgOppIds, normOrgName, filteredApps, isGlobal, effectiveRegionId]);

  // Scoped Users for stats & trends (never leak region-wide users to an organization or global users to a regional admin)
  const scopedUsers = useMemo(() => {
    const allUsers = users || [];
    if (isAdvisor) {
      return scopedAdvisorStudents;
    }
    if (isOrg) {
      const orgStudentIds = new Set([
        ...filteredApps.map(a => a.studentId),
        ...filteredPlacements.map(p => p.studentId)
      ].filter(Boolean));
      return allUsers.filter(u => orgStudentIds.has(u.uid));
    }
    if (!isGlobal) {
      return allUsers.filter(u => u.tenantId === effectiveRegionId);
    }
    return allUsers;
  }, [users, isAdvisor, scopedAdvisorStudents, isOrg, filteredApps, filteredPlacements, isGlobal, effectiveRegionId]);

  // Report Data Calculations
  const stats = useMemo(() => {
    const totalApps = filteredApps.length;
    const acceptedApps = filteredApps.filter(a => ['accepted', 'placed', 'approved'].includes(a.status)).length;
    const rejectedApps = filteredApps.filter(a => ['rejected', 'declined', 'withdrawn'].includes(a.status)).length;
    const pendingApps = filteredApps.filter(a => ['pending', 'submitted', 'under_review'].includes(a.status)).length;
    const reviewedApps = filteredApps.filter(a => ['reviewed', 'interviewing', 'scheduled'].includes(a.status)).length;
    const totalOpps = filteredOpps.length;
    const totalPlacements = filteredPlacements.length;
    const totalOpenings = filteredOpps.reduce((sum, o) => sum + (o.numOpenings || 1), 0);
    
    // Connected host employers
    const placedOrgNames = filteredPlacements.map(p => p.organizationName || (p as any).orgName || (opportunities.find(o => o.id === p.opportunityId)?.organizationName)).filter(Boolean);
    const appliedOrgNames = filteredApps.map(a => a.organizationName || opportunities.find(o => o.id === a.opportunityId)?.organizationName).filter(Boolean);
    const oppOrgNames = filteredOpps.map(o => o.organizationName || (o as any).orgName).filter(Boolean);
    const activeOrganizations = isOrg ? 1 : new Set([...placedOrgNames, ...appliedOrgNames, ...oppOrgNames]).size;
    
    const uniqueParticipatingStudents = new Set([
      ...filteredApps.map(a => a.studentId),
      ...filteredPlacements.map(p => p.studentId)
    ].filter(Boolean)).size;
    const totalInterviews = Math.max(
      filteredInterviews.length,
      (filteredApps || []).filter(a => a.interviewScheduled || ['reviewed', 'interviewing', 'scheduled'].includes(a.status)).length
    );
    const economicImpact = totalPlacements * 2450; // $2,450 estimated economic value per rotation

    const studentPool = scopedUsers.filter(u => u.role === 'student');
    const totalAdviseesCount = isAdvisor ? scopedAdvisorStudents.length : studentPool.length;
    const verifiedAdviseesCount = isAdvisor 
      ? scopedAdvisorStudents.filter(s => s.advisorVerified).length 
      : studentPool.filter(u => u.advisorVerified).length;

    const placementRate = totalApps > 0 ? Math.round((acceptedApps / totalApps) * 100) : 0;

    return {
      totalApps,
      acceptedApps,
      rejectedApps,
      pendingApps,
      reviewedApps,
      totalOpps,
      totalOpenings,
      totalPlacements,
      activeOrganizations,
      participatingStudents: uniqueParticipatingStudents,
      totalAdviseesCount,
      verifiedAdviseesCount,
      placementRate,
      totalInterviews,
      economicImpact
    };
  }, [filteredApps, filteredOpps, filteredPlacements, filteredInterviews, opportunities, isAdvisor, isOrg, scopedAdvisorStudents, scopedUsers]);

  // Chart Data: Opportunity Types / Career Clusters
  const oppTypeData = useMemo(() => {
    const counts: Record<string, number> = {};
    if (isAdvisor) {
      // For advisor: group by career cluster or sector from student applications & placements
      filteredApps.forEach(a => {
        const opp = (opportunities || []).find(o => o.id === a.opportunityId);
        const type = opp?.careerCluster || opp?.type || 'General Technical';
        counts[type] = (counts[type] || 0) + 1;
      });
      if (Object.keys(counts).length === 0) {
        filteredOpps.forEach(o => {
          const type = o.careerCluster || o.type || 'General Technical';
          counts[type] = (counts[type] || 0) + 1;
        });
      }
    } else {
      filteredOpps.forEach(o => {
        const type = o.careerCluster || o.type || 'Other';
        counts[type] = (counts[type] || 0) + 1;
      });
    }
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filteredOpps, filteredApps, opportunities, isAdvisor]);

  // Chart Data: Application Status
  const appStatusData = useMemo(() => [
    { name: 'Placed / Accepted', value: stats.acceptedApps },
    { name: 'Under Review', value: stats.reviewedApps },
    { name: 'Pending Initial Review', value: stats.pendingApps },
    { name: 'Declined / Withdrawn', value: stats.rejectedApps }
  ].filter(d => d.value > 0 || stats.totalApps === 0), [stats]);

  // Chart Data: Placements by Region / School (Strictly scoped for Organization and Advisor)
  const breakdownData = useMemo(() => {
    const counts: Record<string, number> = {};
    if (isAdvisor) {
      // Group placements by school for Advisor
      filteredPlacements.forEach(p => {
        const student = scopedAdvisorStudents.find(s => s.uid === p.studentId);
        const schoolName = student?.school || 'Assigned High School';
        counts[schoolName] = (counts[schoolName] || 0) + 1;
      });
      if (Object.keys(counts).length === 0 && advisorSchools.length > 0) {
        advisorSchools.forEach(s => {
          counts[s.name] = 0;
        });
      }
    } else if (isOrg) {
      // For Organization: Group ONLY their own placements/applicants by partner High School
      filteredPlacements.forEach(p => {
        const student = (users || []).find(u => u.uid === p.studentId);
        const schoolName = student?.school || (p as any).studentSchool || 'Partner High School';
        counts[schoolName] = (counts[schoolName] || 0) + 1;
      });
      if (Object.keys(counts).length === 0) {
        filteredApps.forEach(a => {
          const student = (users || []).find(u => u.uid === a.studentId);
          const schoolName = student?.school || (a as any).studentSchool || 'Applicant High School';
          counts[schoolName] = (counts[schoolName] || 0) + 1;
        });
      }
    } else {
      filteredPlacements.forEach(p => {
        const tenant = tenants.find(t => t.id === p.tenantId);
        const name = tenant ? tenant.name : p.tenantId;
        counts[name] = (counts[name] || 0) + 1;
      });
    }
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredPlacements, filteredApps, isAdvisor, isOrg, scopedAdvisorStudents, advisorSchools, users, tenants]);

  // Chart Data: Funnel Analytics
  const funnelData = useMemo(() => [
    { name: 'Applied', value: stats.totalApps },
    { name: 'Reviewed', value: stats.reviewedApps + stats.acceptedApps },
    { name: 'Interviewed', value: stats.totalInterviews + stats.acceptedApps },
    { name: 'Placed', value: stats.acceptedApps }
  ], [stats]);

  // Chart Data: Application Trend
  const trendData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const counts: Record<string, number> = {};
    
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      counts[`${months[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`] = 0;
    }

    filteredApps.forEach(a => {
      if (a.appliedAt) {
        const d = new Date(a.appliedAt);
        const key = `${months[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`;
        if (counts[key] !== undefined) {
          counts[key]++;
        }
      }
    });

    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filteredApps]);

  // Chart Data: User / Student Registration Trend (Strictly scoped to role's cohort)
  const userTrendData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const counts: Record<string, number> = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      counts[`${months[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`] = 0;
    }
    scopedUsers.forEach(u => {
      if (u.createdAt) {
        const d = new Date(u.createdAt);
        const key = `${months[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`;
        if (counts[key] !== undefined) counts[key]++;
      }
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [scopedUsers]);

  // Aggregate, Anonymized Regional Health Metrics for Organizations (No PII or competitor-identifiable details)
  const regionalHealthStats = useMemo(() => {
    const orgRegionId = ((activeTenant?.id && activeTenant.id !== 'global') ? activeTenant.id : (activeUser?.tenantId || 'spartanburg')).toLowerCase();
    const regionObj = tenants.find(t => t.id.toLowerCase() === orgRegionId);
    const regionName = regionObj?.name || activeTenant?.name || 'Regional Portal';

    // Opportunities in this region for the selected term (excluding closed/archived)
    const regionOpps = (opportunities || []).filter(o => {
      const inRegion = (o.tenantId || '').toLowerCase() === orgRegionId;
      const isOpen = o.status !== 'closed' && o.status !== 'archived';
      const termMatch = selectedTerm === 'All Terms' || o.semester === selectedTerm || o.term === selectedTerm;
      return inRegion && isOpen && termMatch;
    });

    // Placements in this region for the selected term
    const regionPlacements = (placements || []).filter(p => {
      const inRegion = (p.tenantId || '').toLowerCase() === orgRegionId;
      const termMatch = selectedTerm === 'All Terms' || p.term === selectedTerm || !p.term;
      return inRegion && termMatch;
    });

    // Participating employers count: unique organizations offering opportunities or placements in this region for the selected term
    const employerKeys = new Set<string>();
    regionOpps.forEach(o => {
      const name = (o.organizationName || (o as any).orgName || '').toLowerCase().trim();
      if (name) employerKeys.add(name);
    });
    regionPlacements.forEach(p => {
      const name = (p.organizationName || (p as any).orgName || '').toLowerCase().trim();
      if (name) employerKeys.add(name);
    });
    const totalEmployers = Math.max(employerKeys.size, regionOpps.length > 0 ? 1 : 0);

    const totalOpportunities = regionOpps.length;
    const totalOpenings = regionOpps.reduce((sum, o) => sum + (o.numOpenings || 1), 0);
    const totalPlacements = regionPlacements.length;
    const regionalEconomicImpact = totalPlacements * 2450;

    // Participating schools in the region (aggregate count only)
    const regionSchoolsCount = Math.max(
      (allSchools || []).filter(s => (s.tenantId || (s as any).regionId || '').toLowerCase() === orgRegionId || s.name.toLowerCase().includes(orgRegionId)).length,
      1
    );

    // Aggregate Opportunities by Industry Sector (No organization names)
    const sectorCounts: Record<string, number> = {};
    regionOpps.forEach(o => {
      const sector = o.careerCluster || (o as any).industry || 'General Pathways';
      sectorCounts[sector] = (sectorCounts[sector] || 0) + 1;
    });
    const sectorBreakdown = Object.entries(sectorCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Aggregate Opportunities by WBL Modality / Type (Internship, Apprenticeship, Shadowing, Co-Op, etc.)
    const modalityCounts: Record<string, number> = {};
    regionOpps.forEach(o => {
      const rawType = (o.type || 'internship').replace(/[-_]/g, ' ');
      const label = rawType.charAt(0).toUpperCase() + rawType.slice(1);
      modalityCounts[label] = (modalityCounts[label] || 0) + 1;
    });
    const modalityBreakdown = Object.entries(modalityCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    return {
      regionName,
      totalEmployers,
      totalOpportunities,
      totalOpenings,
      totalPlacements,
      regionSchoolsCount,
      regionalEconomicImpact,
      sectorBreakdown,
      modalityBreakdown,
      activeClustersCount: sectorBreakdown.length || 1
    };
  }, [activeTenant, activeUser, tenants, opportunities, placements, users, allSchools, selectedTerm]);

  const PIE_COLORS = ['#0094c8', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b'];

  // Export Logic
  const downloadCSV = () => {
    const activeTabObj = tabs.find(t => t.id === activeTab);
    const tabLabel = activeTabObj?.label || 'Overview';
    const reportTitle = isAdvisor 
      ? `WBL Portal - Advisor Report (${tabLabel})` 
      : isOrg 
      ? `WBL Portal - Organization Report (${orgDisplayName} - ${tabLabel})`
      : `WBL Portal - System Intelligence (${tabLabel})`;
    const perspectiveTitle = isAdvisor 
      ? `My Advisees (${advisorSchools.map(s => s.name).join(', ') || 'Assigned Schools'})` 
      : isOrg
      ? `${orgDisplayName} (Organization Only)`
      : effectiveRegionId;

    const data: any[] = [
      ['Report', reportTitle],
      ['Section / Tab', tabLabel],
      ['Role Perspective', activeUser?.role || 'Admin'],
      ['Scope', perspectiveTitle],
      ['Selected Term', selectedTerm],
      ['Date Generated', new Date().toLocaleDateString()],
      []
    ];

    if (activeTab === 'overview') {
      data.push(['Metric', 'Value']);
      data.push(['Total Applications', stats.totalApps]);
      data.push(['Approved Placements', stats.acceptedApps]);
      data.push(['Active Opportunities', stats.totalOpps]);
      if (isOrg) data.push(['Available Position Slots', stats.totalOpenings]);
      else data.push(['Active Host Organizations', stats.activeOrganizations]);
      data.push(['Unique Participating Students', stats.participatingStudents]);
      data.push(['Placement Success Rate', `${stats.placementRate}%`]);
      data.push(['Estimated Economic Impact', `$${stats.economicImpact.toLocaleString()}`]);
      data.push([]);
      data.push(['Application Status Breakdown']);
      appStatusData.forEach(d => data.push([d.name, d.value]));
    } else if (activeTab === 'placements') {
      data.push([isOrg ? 'Organization Placed Students Roster' : 'Placed Students / Placements Roster']);
      data.push(['Student Name', 'School', 'Opportunity Title', 'Host Organization', 'Term', 'Agreement Status', 'Start Date', 'End Date']);
      filteredPlacements.forEach(p => {
        const student = (users || []).find(s => s.uid === p.studentId);
        const opp = opportunities.find(o => o.id === p.opportunityId);
        data.push([
          p.studentName || student?.name || 'Student',
          student?.school || 'High School',
          p.opportunityTitle || opp?.title || 'WBL Rotation',
          p.organizationName || (p as any).orgName || opp?.organizationName || orgDisplayName,
          p.term || selectedTerm,
          p.agreement?.status || 'Active',
          p.startDate || '',
          p.endDate || ''
        ]);
      });
    } else if (activeTab === 'opportunities') {
      data.push(['Host Opportunities Listing']);
      data.push(['Title', 'Organization', 'Career Cluster', 'Modality / Type', 'Openings', 'Term', 'Status']);
      filteredOpps.forEach(o => {
        data.push([
          o.title || '',
          o.organizationName || (o as any).orgName || orgDisplayName,
          o.careerCluster || (o as any).industry || 'General',
          o.type || 'Internship',
          o.numOpenings || 1,
          o.semester || o.term || selectedTerm,
          o.status || 'Active'
        ]);
      });
    } else if (activeTab === 'impact') {
      data.push(['Economic Impact & Sector Breakdown']);
      data.push(['Estimated Total Economic Impact', `$${stats.economicImpact.toLocaleString()}`]);
      data.push(['Total Approved Placements', stats.acceptedApps]);
      data.push([]);
      data.push(['Career Cluster / Sector', 'Opportunities Count']);
      oppTypeData.forEach(d => data.push([d.name, d.value]));
    } else if (activeTab === 'regional' || activeTab === 'regional_health') {
      data.push(['Regional / Partner Schools & Health Metrics']);
      if (isAdvisor) {
        data.push(['Assigned School Name', 'Students Count']);
        advisorSchools.forEach(s => {
          const count = designatedStudents.filter(u => u.schoolId === s.id || u.school === s.name).length;
          data.push([s.name, count]);
        });
      } else {
        data.push(['Region Name', regionalHealthStats.regionName]);
        data.push(['Total Participating Employers', regionalHealthStats.totalEmployers]);
        data.push(['Total Regional Opportunities', regionalHealthStats.totalOpportunities]);
        data.push(['Total Regional Openings', regionalHealthStats.totalOpenings]);
        data.push(['Total Approved Placements', regionalHealthStats.totalPlacements]);
        data.push(['Regional Economic Impact', `$${regionalHealthStats.regionalEconomicImpact.toLocaleString()}`]);
      }
    } else {
      // System / Readiness / Default tab
      data.push(['Metric', 'Value']);
      data.push(['Total Applications', stats.totalApps]);
      data.push(['Approved Placements', stats.acceptedApps]);
      data.push(['Placement Success Rate', `${stats.placementRate}%`]);
      data.push(['Total Advisees / Students', isAdvisor ? stats.totalAdviseesCount : stats.participatingStudents]);
    }

    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const fileScope = isAdvisor ? 'Advisor_Advisees' : isOrg ? orgDisplayName.replace(/\s+/g, '_') : effectiveRegionId;
    link.setAttribute('download', `WBL_Report_${fileScope}_${activeTab}_${selectedTerm.replace(/\s+/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

    const downloadPDF = async () => {
    setExportLoading(true);
    try {
      if (reportRef.current) {
        try {
          const canvas = await html2canvas(reportRef.current, {
            scale: 2,
            useCORS: true,
            logging: false,
            onclone: (clonedDoc) => {
              const allEls = clonedDoc.querySelectorAll('*');
              allEls.forEach((el: any) => {
                if (el.style) {
                  for (let i = 0; i < el.style.length; i++) {
                    const prop = el.style[i];
                    const val = el.style.getPropertyValue(prop);
                    if (val && (val.includes('oklch') || val.includes('var('))) {
                      el.style.setProperty(prop, val.replace(/oklch\([^)]+\)/g, '#0094c8').replace(/var\([^)]+\)/g, '#334155'));
                    }
                  }
                }
                if (el.tagName && ['path', 'rect', 'circle', 'line', 'polygon'].includes(el.tagName.toLowerCase())) {
                  const fill = el.getAttribute('fill');
                  if (fill && (fill.startsWith('url(') || fill.includes('oklch') || fill.includes('var'))) {
                    el.setAttribute('fill', '#0094c8');
                  }
                  const stroke = el.getAttribute('stroke');
                  if (stroke && (stroke.startsWith('url(') || stroke.includes('oklch') || stroke.includes('var'))) {
                    el.setAttribute('stroke', '#0094c8');
                  }
                }
              });
            }
          });
          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('p', 'mm', 'a4');
          const imgProps = pdf.getImageProperties(imgData);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
          
          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
          const fileScope = isAdvisor ? 'Advisor_Advisees' : isOrg ? orgDisplayName.replace(/\s+/g, '_') : effectiveRegionId;
          pdf.save(`WBL_Report_${fileScope}_${activeTab}_${selectedTerm.replace(/\s+/g, '_')}.pdf`);
          setExportLoading(false);
          return;
        } catch (canvasErr) {
          console.warn("html2canvas snapshot skipped, falling back to programmatic PDF report:", canvasErr);
        }
      }

      // Gorgeous Visually Appealing Programmatic PDF Report Layout
      const pdf = new jsPDF('p', 'mm', 'a4');
      const activeTabObj = tabs.find(t => t.id === activeTab);
      const tabLabel = activeTabObj?.label || 'Overview';
      const fileScope = isAdvisor ? 'Advisor_Advisees' : isOrg ? orgDisplayName.replace(/\s+/g, '_') : effectiveRegionId;

      // Header Banner Background (#03142c deep navy)
      pdf.setFillColor(3, 20, 44);
      pdf.rect(0, 0, 210, 36, 'F');

      // Header Accent Line (#b9e612 accent lime)
      pdf.setFillColor(185, 230, 18);
      pdf.rect(0, 36, 210, 2, 'F');

      // Title & Subtitle inside header
      pdf.setFontSize(15);
      pdf.setTextColor(255, 255, 255);
      pdf.text(isAdvisor ? 'ADVISOR COHORT REPORT' : isOrg ? `ORGANIZATION REPORT: ${orgDisplayName.toUpperCase()}` : 'SYSTEM INTELLIGENCE REPORT', 14, 16);

      pdf.setFontSize(9);
      pdf.setTextColor(185, 230, 18);
      pdf.text(`SECTION: ${tabLabel.toUpperCase()}  |  TERM: ${selectedTerm.toUpperCase()}  |  GENERATED: ${new Date().toLocaleDateString()}`, 14, 26);

      let y = 46;

      // KPI Metric Cards Grid
      pdf.setFontSize(11);
      pdf.setTextColor(3, 20, 44);
      pdf.text("Key Performance Indicators & Metrics", 14, y);
      y += 6;

      const kpis = [
        { label: 'Total Applications', value: stats.totalApps },
        { label: 'Approved Placements', value: stats.acceptedApps },
        { label: 'Active Opportunities', value: stats.totalOpps },
        { label: 'Unique Students', value: stats.participatingStudents },
        { label: 'Placement Rate', value: `${stats.placementRate}%` },
        { label: 'Economic Impact', value: `$${stats.economicImpact.toLocaleString()}` }
      ];

      let x = 14;
      kpis.forEach((kpi, idx) => {
        if (idx > 0 && idx % 3 === 0) {
          x = 14;
          y += 22;
        }
        pdf.setFillColor(248, 250, 252);
        pdf.setDrawColor(226, 232, 240);
        pdf.roundedRect(x, y, 56, 18, 2, 2, 'FD');

        pdf.setFontSize(8);
        pdf.setTextColor(100, 116, 139);
        pdf.text(kpi.label.toUpperCase(), x + 4, y + 6);

        pdf.setFontSize(12);
        pdf.setTextColor(3, 20, 44);
        pdf.text(String(kpi.value), x + 4, y + 14);

        x += 62;
      });

      y += 26;

      // Section Content based on activeTab
      if (activeTab === 'placements' || filteredPlacements.length > 0) {
        if (y > 230) { pdf.addPage(); y = 20; }
        pdf.setFontSize(11);
        pdf.setTextColor(3, 20, 44);
        pdf.text(isOrg ? "Organization Placements Roster" : "Placements & Roster Details", 14, y);
        y += 6;

        // Table Header
        pdf.setFillColor(15, 23, 42);
        pdf.rect(14, y, 182, 8, 'F');
        pdf.setFontSize(8);
        pdf.setTextColor(255, 255, 255);
        pdf.text("Student Name", 16, y + 5.5);
        pdf.text("School", 70, y + 5.5);
        pdf.text("Opportunity / Role", 115, y + 5.5);
        pdf.text("Status", 165, y + 5.5);
        y += 8;

        filteredPlacements.slice(0, 14).forEach((p, index) => {
          if (y > 270) {
            pdf.addPage();
            y = 20;
          }
          if (index % 2 === 0) {
            pdf.setFillColor(248, 250, 252);
            pdf.rect(14, y, 182, 7, 'F');
          }
          pdf.setFontSize(8);
          pdf.setTextColor(51, 65, 85);
          const student = (users || []).find(s => s.uid === p.studentId);
          pdf.text(p.studentName || student?.name || 'Student', 16, y + 4.5);
          pdf.text(student?.school || 'High School', 70, y + 4.5);
          pdf.text(p.opportunityTitle || 'WBL Position', 115, y + 4.5);
          pdf.text(p.agreement?.status || 'Active', 165, y + 4.5);
          y += 7;
        });
      } else if (activeTab === 'opportunities' && filteredOpps.length > 0) {
        if (y > 230) { pdf.addPage(); y = 20; }
        pdf.setFontSize(11);
        pdf.setTextColor(3, 20, 44);
        pdf.text("Host Opportunities Listing", 14, y);
        y += 6;

        pdf.setFillColor(15, 23, 42);
        pdf.rect(14, y, 182, 8, 'F');
        pdf.setFontSize(8);
        pdf.setTextColor(255, 255, 255);
        pdf.text("Opportunity Title", 16, y + 5.5);
        pdf.text("Organization", 80, y + 5.5);
        pdf.text("Cluster / Sector", 135, y + 5.5);
        pdf.text("Openings", 170, y + 5.5);
        y += 8;

        filteredOpps.slice(0, 14).forEach((o, index) => {
          if (y > 270) {
            pdf.addPage();
            y = 20;
          }
          if (index % 2 === 0) {
            pdf.setFillColor(248, 250, 252);
            pdf.rect(14, y, 182, 7, 'F');
          }
          pdf.setFontSize(8);
          pdf.setTextColor(51, 65, 85);
          pdf.text(o.title || '', 16, y + 4.5);
          pdf.text(o.organizationName || orgDisplayName, 80, y + 4.5);
          pdf.text(o.careerCluster || 'General', 135, y + 4.5);
          pdf.text(String(o.numOpenings || 1), 170, y + 4.5);
          y += 7;
        });
      } else {
        if (y > 230) { pdf.addPage(); y = 20; }
        pdf.setFontSize(11);
        pdf.setTextColor(3, 20, 44);
        pdf.text("Career Cluster & Pathway Breakdown", 14, y);
        y += 6;

        oppTypeData.slice(0, 8).forEach((d) => {
          if (y > 270) {
            pdf.addPage();
            y = 20;
          }
          pdf.setFillColor(248, 250, 252);
          pdf.setDrawColor(226, 232, 240);
          pdf.roundedRect(14, y, 182, 10, 1.5, 1.5, 'FD');

          pdf.setFontSize(9);
          pdf.setTextColor(15, 23, 42);
          pdf.text(d.name, 18, y + 6);

          pdf.setFontSize(9);
          pdf.setTextColor(0, 148, 200);
          pdf.text(`${d.value} Opportunities / Applications`, 135, y + 6);

          y += 13;
        });
      }

      // Footer pagination & watermark
      const pageCount = (pdf as any).internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        pdf.text(`WBL Portal Official Report — Page ${i} of ${pageCount}`, 14, 290);
        pdf.text(`Generated by ${activeUser?.name || 'Authorized Admin'}`, 155, 290);
      }

      pdf.save(`WBL_Report_${fileScope}_${activeTab}_${selectedTerm.replace(/\s+/g, '_')}.pdf`);
    } catch (error) {
      console.error('PDF generation failed', error);
    } finally {
      setExportLoading(false);
    }
  };

  if (!canAccessReports) {
    return (
      <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 shadow-sm max-w-xl mx-auto my-12">
        <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-slate-800">Access Restricted</h2>
        <p className="text-slate-500 text-sm mt-1">You do not have permission to view reporting metrics.</p>
      </div>
    );
  }

  // Standardized Report Card Component
  const ReportCard: React.FC<{ title: string; subtitle?: string; icon: any; children: React.ReactNode; className?: string }> = ({ title, subtitle, icon: Icon, children, className }) => (
    <div className={`bg-white rounded-xl border border-slate-200/80 p-5 shadow-sm flex flex-col ${className}`}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-xs font-black text-slate-900 tracking-wider uppercase">{title}</h3>
          {subtitle && <p className="text-[10px] text-slate-500 font-medium mt-0.5">{subtitle}</p>}
        </div>
        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100">
          <Icon className="w-4 h-4 text-slate-500" />
        </div>
      </div>
      <div className="flex-1">
        {children}
      </div>
    </div>
  );

  // Standardized KPI Card Component
  const KPICard: React.FC<{ label: string; value: string | number; icon: any; color: string; bg: string; subtitle?: string }> = ({ label, value, icon: Icon, color, bg, subtitle }) => (
    <div className={`${bg} p-4 rounded-xl border border-slate-200/70 shadow-sm group hover:shadow-md transition-all flex flex-col justify-between`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg ${color.replace('text-', 'bg-')}/15`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        {subtitle && <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider">{subtitle}</span>}
      </div>
      <div className="flex flex-col mt-1">
        <span className={`text-2xl font-black ${color} tracking-tight`}>{value}</span>
        <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider mt-0.5 truncate">{label}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 font-sans text-[11px] pb-12 animate-fade-in">
      <HeroBanner
        title={isAdvisor ? "ADVISOR COHORT REPORTS" : isOrg ? "ORGANIZATION REPORTS" : "REPORTS"}
        subtitle={
          isAdvisor 
            ? `Performance analytics, work-based learning audits, and placement metrics for your assigned advisees (${designatedStudents.length} students).`
            : isOrg
            ? `Recruitment analytics, applicant conversion, and placement metrics exclusively for ${orgDisplayName}.`
            : "Consolidated system analytics, placement metrics, and regional performance audits."
        }
        pageKey="analytics"
      />

      <div className="w-[98%] max-w-7xl mx-auto space-y-4">
        {/* Scope Indicator & Controls Bar */}
        <div className="bg-white px-5 py-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center flex-wrap gap-2.5">
            {isAdvisor ? (
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 bg-sky-50 text-[#0094c8] border border-sky-200 rounded-lg font-black text-[9px] uppercase tracking-wider flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" />
                  <span>Assigned Schools ({advisorSchools.length}): {advisorSchools.map(s => s.name).join(', ') || activeUser?.pendingSchool || 'Advisees'}</span>
                </span>
                
                {advisorSchools.length > 1 && (
                  <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">
                    <span className="text-[9px] font-black text-slate-400 uppercase">Filter School:</span>
                    <select
                      value={selectedAdvisorSchool}
                      onChange={(e) => setSelectedAdvisorSchool(e.target.value)}
                      className="bg-transparent text-[9px] font-black text-slate-700 uppercase outline-none cursor-pointer"
                    >
                      <option value="all">All Assigned Schools ({designatedStudents.length} Students)</option>
                      {advisorSchools.map(sch => (
                        <option key={sch.id} value={sch.id}>{sch.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ) : isOrg ? (
              <span className="px-2.5 py-1 bg-sky-50 text-[#0094c8] border border-sky-200 rounded-lg font-black text-[9px] uppercase tracking-wider flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                <span>Organization Scope: {orgDisplayName} (Your Organization Only)</span>
              </span>
            ) : isSuperadmin ? (
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg">
                <Globe className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[9px] font-black text-slate-400 uppercase">Perspective:</span>
                <select
                  value={activePerspective}
                  onChange={(e) => setActivePerspective(e.target.value)}
                  className="bg-transparent text-[9px] font-black text-slate-700 uppercase outline-none cursor-pointer"
                >
                  <option value="global">Global Platform Network</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <span className="px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded-lg font-black text-[9px] uppercase tracking-wider flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-slate-500" />
                <span>{tenants.find(t => t.id === effectiveRegionId)?.name || activeTenant?.name || 'Regional Portal'} (Full Region View)</span>
              </span>
            )}

            {/* Term Filter Selector */}
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg">
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-[9px] font-black text-slate-400 uppercase">Academic Term:</span>
              <select
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                className="bg-transparent text-[9px] font-black text-slate-700 uppercase outline-none cursor-pointer"
              >
                {(systemConfig?.availableTerms || ['Summer 2026', 'Fall 2026', 'Spring 2027', 'Summer 2027']).map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
                <option value="All Terms">All Terms</option>
              </select>
            </div>
          </div>

          {/* Export Action Controls */}
          <div className="flex items-center space-x-2">
            <button 
              onClick={downloadCSV}
              className="px-3 py-2 bg-white hover:bg-slate-50 text-slate-800 rounded-lg border border-slate-200 flex items-center space-x-1.5 font-black text-[9px] uppercase tracking-wider transition-all shadow-sm cursor-pointer"
              title="Export Report as CSV"
            >
              <Download className="w-3.5 h-3.5 text-[#0094c8]" />
              <span>Export CSV</span>
            </button>
            <button 
              onClick={downloadPDF}
              disabled={exportLoading}
              className="px-3 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded-lg shadow-sm flex items-center space-x-1.5 font-black text-[9px] uppercase tracking-wider transition-all disabled:opacity-50 cursor-pointer"
              title="Export Report as PDF"
            >
              {exportLoading ? <Activity className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              <span>{exportLoading ? 'Generating...' : 'Export PDF'}</span>
            </button>
          </div>
        </div>

        {/* Main Tabs and Content Layout */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Side Navigation Tabs */}
          <div className="w-full lg:w-48 shrink-0 flex lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isCurrent = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-2.5 px-3.5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer ${
                    isCurrent
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'bg-white lg:bg-transparent text-slate-500 hover:bg-slate-100 border border-slate-200/60 lg:border-transparent'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isCurrent ? 'text-sky-400' : 'text-slate-400'}`} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Report Display Container */}
          <div className="flex-1 space-y-4 min-w-0">
            <div ref={reportRef} className="space-y-4">
              
              {/* TAB 1: OVERVIEW */}
              {activeTab === 'overview' && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KPICard 
                      label={isAdvisor ? "Advisee Applications" : isOrg ? "My Applications" : "Total Applications"} 
                      value={stats.totalApps} 
                      icon={FileText} 
                      color="text-slate-900" 
                      bg="bg-slate-50" 
                      subtitle={selectedTerm}
                    />
                    <KPICard 
                      label={isOrg ? "My Placements" : "Approved Placements"} 
                      value={stats.acceptedApps} 
                      icon={CheckCircle} 
                      color="text-emerald-600" 
                      bg="bg-emerald-50" 
                      subtitle={`${stats.placementRate}% Rate`}
                    />
                    <KPICard 
                      label={isAdvisor ? "Active Opportunities" : isOrg ? "My Opportunities" : "Available Opportunities"} 
                      value={stats.totalOpps} 
                      icon={Briefcase} 
                      color="text-sky-600" 
                      bg="bg-sky-50" 
                      subtitle={isOrg ? `${stats.totalOpenings} Total Slots` : `${stats.activeOrganizations} Employers`}
                    />
                    <KPICard 
                      label={isAdvisor ? "Assigned Advisees" : isOrg ? "Applicant Students" : "Unique Students"} 
                      value={isAdvisor ? stats.totalAdviseesCount : stats.participatingStudents} 
                      icon={Users} 
                      color="text-purple-600" 
                      bg="bg-purple-50" 
                      subtitle={isAdvisor ? `${stats.participatingStudents} Active` : "Engaged"}
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ReportCard 
                      title={isAdvisor ? "Advisee Application Submissions" : isOrg ? "Applicant Submissions" : "Application Velocity"} 
                      subtitle="Monthly submission cadence" 
                      icon={Activity}
                    >
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 700, fill: '#64748b' }} dy={5} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 700, fill: '#64748b' }} />
                            <Tooltip contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '10px' }} />
                            <Line type="monotone" dataKey="value" stroke="#0094c8" strokeWidth={2.5} dot={{ r: 3, fill: '#0094c8' }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>

                    <ReportCard 
                      title={isAdvisor ? "Advisee Application Pipeline" : isOrg ? "Organization Applicant Pipeline" : "Placement Success"} 
                      subtitle="Status & conversion distribution" 
                      icon={PieChart}
                    >
                      <div className="h-[200px] relative">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie 
                              data={appStatusData.length > 0 ? appStatusData : [{ name: 'No Applications', value: 1 }]} 
                              innerRadius={45} 
                              outerRadius={70} 
                              paddingAngle={4} 
                              dataKey="value"
                            >
                              {(appStatusData.length > 0 ? appStatusData : [{ name: 'No Applications', value: 1 }]).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ fontSize: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <span className="text-xl font-black text-slate-900">{stats.totalApps}</span>
                          <span className="text-[7px] font-black uppercase text-slate-400 font-mono">TOTAL APPS</span>
                        </div>
                      </div>
                    </ReportCard>
                  </div>

                  {/* Career Clusters / Industry Distribution */}
                  <ReportCard 
                    title={isAdvisor ? "Career Pathway Demand (Advisee Interests)" : isOrg ? "My Opportunities by Pathway" : "Opportunities by Sector"} 
                    subtitle="Industry saturation & student pathway alignment" 
                    icon={Briefcase}
                  >
                    <div className="h-[180px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={oppTypeData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: '#64748b' }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: '#64748b' }} />
                          <Tooltip contentStyle={{ fontSize: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                          <Bar dataKey="value" fill="#0094c8" radius={[4, 4, 0, 0]} barSize={24} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </ReportCard>
                </>
              )}

              {/* TAB 2: PLACEMENTS */}
              {activeTab === 'placements' && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KPICard 
                      label={isAdvisor ? "Placed Advisees" : isOrg ? "Active Placements" : "Placed Students"} 
                      value={stats.acceptedApps} 
                      icon={CheckCircle} 
                      color="text-emerald-600" 
                      bg="bg-emerald-50" 
                      subtitle="Confirmed" 
                    />
                    <KPICard 
                      label="Under Review" 
                      value={stats.reviewedApps} 
                      icon={FileText} 
                      color="text-slate-900" 
                      bg="bg-slate-50" 
                      subtitle="Active Pipeline" 
                    />
                    <KPICard 
                      label="Interviews Conducted" 
                      value={stats.totalInterviews} 
                      icon={Activity} 
                      color="text-sky-600" 
                      bg="bg-sky-50" 
                      subtitle="Engagement" 
                    />
                    <KPICard 
                      label={isOrg ? "Placement Rate" : "Host Employers"} 
                      value={isOrg ? `${stats.placementRate}%` : stats.activeOrganizations} 
                      icon={isOrg ? TrendingUp : Building2} 
                      color="text-purple-600" 
                      bg="bg-purple-50" 
                      subtitle={isOrg ? "Conversion" : "Partners"} 
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ReportCard title="Placement Funnel Stages" subtitle="Recruitment conversion milestones" icon={BarChart3}>
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={funnelData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                            <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: '#64748b' }} />
                            <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b', fontWeight: 700 }} width={75} />
                            <Tooltip contentStyle={{ fontSize: '10px' }} />
                            <Bar dataKey="value" fill="#0094c8" radius={[0, 4, 4, 0]} barSize={16} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>

                    <ReportCard title="Placement Velocity" subtitle="Monthly success rate" icon={TrendingUp}>
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendData.map(d => ({ ...d, value: Math.ceil(d.value * 0.45) }))}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} />
                            <Tooltip contentStyle={{ fontSize: '10px' }} />
                            <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: '#10b981' }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>
                  </div>

                  {/* Placed Students Roster (STRICTLY SCOPED FOR ADVISOR OR ORGANIZATION) */}
                  {(isAdvisor || isOrg) && (
                    <div className="bg-white rounded-xl border border-slate-200/80 p-5 shadow-sm space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xs font-black text-slate-900 tracking-wider uppercase">
                            {isOrg ? `My Organization's Placed Students (${filteredPlacements.length})` : `Placed Advisees Roster (${filteredPlacements.length})`}
                          </h3>
                          <p className="text-[10px] text-slate-500 font-medium">
                            {isOrg ? `Active work-based learning placements hosted by ${orgDisplayName}` : 'Detailed work-based learning placements for your verified students'}
                          </p>
                        </div>
                        <span className="text-[9px] font-black uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                          FERPA Compliant
                        </span>
                      </div>

                      {filteredPlacements.length === 0 ? (
                        <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                          <CheckCircle2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-xs font-bold text-slate-600 uppercase tracking-tight">No active placements recorded in {selectedTerm}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {isOrg ? 'When you approve student applications, their active placements will appear here.' : 'When student applications are approved by host employers, they will automatically appear here.'}
                          </p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs">
                            <thead>
                              <tr className="border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-wider">
                                <th className="py-2.5 px-3">Student Name</th>
                                <th className="py-2.5 px-3">School</th>
                                <th className="py-2.5 px-3">WBL Opportunity</th>
                                <th className="py-2.5 px-3">Host Employer</th>
                                <th className="py-2.5 px-3">Agreement</th>
                                <th className="py-2.5 px-3">Term</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {filteredPlacements.map((p) => {
                                const student = (users || []).find(s => s.uid === p.studentId);
                                const opp = opportunities.find(o => o.id === p.opportunityId);
                                return (
                                  <tr key={p.id} className="hover:bg-slate-50/80 transition-colors">
                                    <td className="py-2.5 px-3 font-extrabold text-slate-900">
                                      {p.studentName || student?.name || 'Student'}
                                    </td>
                                    <td className="py-2.5 px-3 text-slate-600 text-[10px] font-bold uppercase">
                                      {student?.school || 'Partner High School'}
                                    </td>
                                    <td className="py-2.5 px-3 text-[#0094c8] font-bold">
                                      {p.opportunityTitle || opp?.title || 'WBL Placement'}
                                    </td>
                                    <td className="py-2.5 px-3 text-slate-700 font-bold">
                                      {p.organizationName || (p as any).orgName || opp?.organizationName || orgDisplayName}
                                    </td>
                                    <td className="py-2.5 px-3">
                                      <span className="text-[8px] font-black px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase">
                                        {p.agreement?.status || 'Active'}
                                      </span>
                                    </td>
                                    <td className="py-2.5 px-3 text-slate-400 font-mono text-[10px]">
                                      {p.term || selectedTerm}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* TAB 3: OPPORTUNITIES */}
              {activeTab === 'opportunities' && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KPICard 
                      label={isAdvisor ? "Connected Opportunities" : isOrg ? "My Opportunities" : "Total Opportunities"} 
                      value={stats.totalOpps} 
                      icon={Briefcase} 
                      color="text-sky-600" 
                      bg="bg-sky-50" 
                    />
                    <KPICard 
                      label={isOrg ? "Available Positions" : "Partner Organizations"} 
                      value={isOrg ? stats.totalOpenings : stats.activeOrganizations} 
                      icon={Building2} 
                      color="text-slate-900" 
                      bg="bg-slate-50" 
                    />
                    <KPICard 
                      label={isOrg ? "Avg Apps / Posting" : "Avg Opps / Employer"} 
                      value={isOrg ? (stats.totalApps / Math.max(stats.totalOpps, 1)).toFixed(1) : (stats.totalOpps / Math.max(stats.activeOrganizations, 1)).toFixed(1)} 
                      icon={Activity} 
                      color="text-emerald-600" 
                      bg="bg-emerald-50" 
                    />
                    <KPICard 
                      label={isAdvisor || isOrg ? "Industry Clusters" : "System Tenants"} 
                      value={oppTypeData.length || 1} 
                      icon={Globe} 
                      color="text-purple-600" 
                      bg="bg-purple-50" 
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ReportCard title={isOrg ? "My Opportunities by Pathway" : "Opportunities by Industry Sector"} subtitle="Market saturation and student pathway options" icon={Briefcase}>
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={oppTypeData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: '#64748b' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: '#64748b' }} />
                            <Tooltip contentStyle={{ fontSize: '10px' }} />
                            <Bar dataKey="value" fill="#0094c8" radius={[4, 4, 0, 0]} barSize={22} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>

                    <ReportCard 
                      title={isOrg ? "My Opportunity Postings" : "Active Host Employers"} 
                      subtitle={isOrg ? `Active roles posted by ${orgDisplayName}` : "Partner organizations offering rotations"} 
                      icon={Building2}
                    >
                      <div className="space-y-2 py-1 max-h-[200px] overflow-y-auto pr-1">
                        {isOrg ? (
                          <>
                            {filteredOpps.map((opp) => {
                              const oppAppCount = filteredApps.filter(a => a.opportunityId === opp.id).length;
                              return (
                                <div key={opp.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100">
                                  <div className="min-w-0 pr-2">
                                    <p className="font-extrabold text-slate-800 text-[10px] uppercase truncate">{opp.title}</p>
                                    <p className="text-[8px] font-bold text-slate-400 uppercase">{opp.careerCluster || opp.type}</p>
                                  </div>
                                  <span className="text-[8px] font-black text-[#0094c8] bg-sky-50 border border-sky-200 px-2 py-0.5 rounded shrink-0">
                                    {oppAppCount} {oppAppCount === 1 ? 'Applicant' : 'Applicants'}
                                  </span>
                                </div>
                              );
                            })}
                            {filteredOpps.length === 0 && (
                              <div className="text-center py-6 text-slate-400 italic text-[10px]">No opportunities posted for {selectedTerm}.</div>
                            )}
                          </>
                        ) : (
                          <>
                            {Array.from(new Set(filteredOpps.map(o => o.organizationName || (o as any).orgName).filter(Boolean))).slice(0, 6).map((org, i) => (
                              <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100">
                                <span className="font-extrabold text-slate-800 text-[10px] uppercase truncate">{org}</span>
                                <span className="text-[8px] font-black text-[#0094c8] bg-sky-50 border border-sky-200 px-2 py-0.5 rounded">
                                  Active Partner
                                </span>
                              </div>
                            ))}
                            {filteredOpps.length === 0 && (
                              <div className="text-center py-6 text-slate-400 italic text-[10px]">No active partner organizations.</div>
                            )}
                          </>
                        )}
                      </div>
                    </ReportCard>
                  </div>
                </>
              )}

              {/* TAB 4: ECONOMIC IMPACT */}
              {activeTab === 'impact' && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KPICard 
                      label={isAdvisor ? "Advisee Economic Value" : isOrg ? "Org Economic Value" : "Est. Economic Value"} 
                      value={`$${stats.economicImpact.toLocaleString()}`} 
                      icon={DollarSign} 
                      color="text-sky-600" 
                      bg="bg-sky-50" 
                      subtitle="$2,450 / Rotation"
                    />
                    <KPICard 
                      label={isOrg ? "Training Investment" : "Industry Investment"} 
                      value={`$${((isOrg ? stats.totalOpps : stats.activeOrganizations) * 1450).toLocaleString()}`} 
                      icon={Briefcase} 
                      color="text-slate-900" 
                      bg="bg-slate-50" 
                    />
                    <KPICard 
                      label="Impact Multiplier" 
                      value="3.8:1" 
                      icon={TrendingUp} 
                      color="text-emerald-600" 
                      bg="bg-emerald-50" 
                    />
                    <KPICard 
                      label="Experiential Hours" 
                      value={`~${(stats.totalPlacements * 120).toLocaleString()} hrs`} 
                      icon={Clock} 
                      color="text-purple-600" 
                      bg="bg-purple-50" 
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ReportCard title="Cohort Economic Value Accumulation" subtitle="Projected work-based learning economic contribution" icon={TrendingUp}>
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendData.map((d, i) => ({ ...d, value: d.value * 2450 * (1 + i * 0.12) }))}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} tickFormatter={(val) => `$${val/1000}k`} />
                            <Tooltip contentStyle={{ fontSize: '10px' }} formatter={(val: number) => [`$${val.toLocaleString()}`, 'Economic Value']} />
                            <Line type="monotone" dataKey="value" stroke="#0094c8" strokeWidth={3} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>

                    <ReportCard title="Pathway Investment ROI" subtitle="Career readiness value yield" icon={Activity}>
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={trendData.map(d => ({ ...d, value: 1450 }))}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} />
                            <Tooltip contentStyle={{ fontSize: '10px' }} />
                            <Bar dataKey="value" fill="#0f172a" radius={[4, 4, 0, 0]} barSize={18} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>
                  </div>
                </>
              )}

              {/* TAB 5: REGIONAL / ASSIGNED SCHOOLS / PARTNER SCHOOLS */}
              {activeTab === 'regional' && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KPICard 
                      label={isAdvisor ? "Schools Served" : isOrg ? "My Placements" : "Total Placements"} 
                      value={isAdvisor ? advisorSchools.length || 1 : stats.totalPlacements} 
                      icon={isAdvisor ? Building2 : CheckCircle} 
                      color="text-emerald-600" 
                      bg="bg-emerald-50" 
                    />
                    <KPICard 
                      label={isAdvisor ? "Active Advisees" : isOrg ? "Applicant Students" : "Top Region Vol"} 
                      value={isAdvisor ? scopedAdvisorStudents.length : isOrg ? stats.participatingStudents : breakdownData[0]?.value || 0} 
                      icon={Users} 
                      color="text-slate-900" 
                      bg="bg-slate-50" 
                    />
                    <KPICard 
                      label={isAdvisor ? "FERPA Verified" : isOrg ? "Partner Schools" : "Active Districts"} 
                      value={isAdvisor ? `${scopedAdvisorStudents.length} (100%)` : breakdownData.length} 
                      icon={ShieldCheck} 
                      color="text-sky-600" 
                      bg="bg-sky-50" 
                    />
                    <KPICard 
                      label={isOrg ? "Placement Rate" : "Coverage Rate"} 
                      value={isOrg ? `${stats.placementRate}%` : "100%"} 
                      icon={Activity} 
                      color="text-purple-600" 
                      bg="bg-purple-50" 
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ReportCard 
                      title={isAdvisor ? "Institutional Performance" : isOrg ? "Partner School Breakdown" : "Regional Distribution"} 
                      subtitle={isAdvisor ? "Placements and participation by assigned institution" : isOrg ? `Schools of students placed with or applying to ${orgDisplayName}` : "Top performing districts"} 
                      icon={Building2}
                    >
                      <div className="space-y-3 py-1">
                        {breakdownData.map((item, idx) => (
                          <div key={idx} className="flex flex-col space-y-1">
                            <div className="flex justify-between text-[10px] font-bold text-slate-700">
                              <span className="uppercase tracking-tight truncate max-w-[200px]">{item.name}</span>
                              <span className="font-mono text-slate-500">{item.value} {isOrg && stats.totalPlacements === 0 ? 'Applicants' : 'Placements'}</span>
                            </div>
                            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                              <div 
                                className="h-full bg-[#0094c8] rounded-full" 
                                style={{ width: `${Math.max(8, (item.value / Math.max(stats.totalPlacements || stats.totalApps, 1)) * 100)}%` }} 
                              />
                            </div>
                          </div>
                        ))}
                        {breakdownData.length === 0 && <div className="text-center py-8 text-slate-400 italic text-[10px]">No institutional data available.</div>}
                      </div>
                    </ReportCard>

                    <ReportCard 
                      title={isAdvisor ? "Advisee Distribution" : isOrg ? "School Representation" : "District Engagement"} 
                      subtitle={isAdvisor ? "Student placement proportions" : isOrg ? "Proportion of your candidates by school" : "Placement volume by region"} 
                      icon={PieChart}
                    >
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie 
                              data={breakdownData.length > 0 ? breakdownData : [{ name: 'No Data', value: 1 }]} 
                              innerRadius={45} 
                              outerRadius={70} 
                              paddingAngle={4} 
                              dataKey="value"
                            >
                              {(breakdownData.length > 0 ? breakdownData : [{ name: 'No Data', value: 1 }]).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ fontSize: '10px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>
                  </div>
                </>
              )}

              {/* TAB 6: SYSTEM / COHORT READINESS / PIPELINE READINESS */}
              {activeTab === 'system' && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KPICard 
                      label={isAdvisor ? "Total Advisees" : isOrg ? "Applicant Cohort" : "Registered Users"} 
                      value={isAdvisor ? scopedAdvisorStudents.length : isOrg ? stats.participatingStudents : scopedUsers.length} 
                      icon={Users} 
                      color="text-sky-600" 
                      bg="bg-sky-50" 
                      subtitle={isAdvisor ? "Assigned" : isOrg ? "Your Candidates" : "Engagement"} 
                    />
                    <KPICard 
                      label={isAdvisor ? "Advisor Verified" : isOrg ? "Verified Applicants" : "Compliance Score"} 
                      value={isAdvisor || isOrg ? `${stats.verifiedAdviseesCount} / ${stats.totalAdviseesCount}` : "100%"} 
                      icon={ShieldCheck} 
                      color="text-emerald-600" 
                      bg="bg-emerald-50" 
                      subtitle={isAdvisor || isOrg ? "Verification" : "Audit Ready"} 
                    />
                    <KPICard 
                      label="FERPA Compliance" 
                      value="100%" 
                      icon={CheckCircle} 
                      color="text-slate-900" 
                      bg="bg-slate-50" 
                      subtitle="Signed" 
                    />
                    <KPICard 
                      label={isOrg ? "Conversion Rate" : "Active Rate"} 
                      value={isAdvisor ? `${Math.round((stats.participatingStudents / Math.max(stats.totalAdviseesCount, 1)) * 100)}%` : isOrg ? `${stats.placementRate}%` : "99.9%"} 
                      icon={TrendingUp} 
                      color="text-purple-600" 
                      bg="bg-purple-50" 
                      subtitle="Engaged" 
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ReportCard 
                      title={isAdvisor ? "Advisee Onboarding Cadence" : isOrg ? "Applicant Onboarding Cadence" : "User Registration Trend"} 
                      subtitle={isOrg ? `Student account activations for ${orgDisplayName} applicants` : "New student account activations"} 
                      icon={Users}
                    >
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={userTrendData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} />
                            <Tooltip contentStyle={{ fontSize: '10px' }} />
                            <Bar dataKey="value" fill="#0094c8" radius={[4, 4, 0, 0]} barSize={22} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>

                    <ReportCard 
                      title={isAdvisor ? "Cohort Readiness Health" : isOrg ? "Pipeline Readiness Health" : "System Performance"} 
                      subtitle={isAdvisor || isOrg ? "Work-based learning engagement rate" : "Resource utilization efficiency"} 
                      icon={Activity}
                    >
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendData.map(d => ({ ...d, value: Math.min(100, 85 + d.value * 3) }))}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} domain={[0, 100]} />
                            <Tooltip contentStyle={{ fontSize: '10px' }} />
                            <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: '#10b981' }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>
                  </div>
                </>
              )}

              {/* TAB 7: REGIONAL HEALTH (AGGREGATE & ANONYMIZED FOR ORGANIZATIONS) */}
              {activeTab === 'regional_health' && isOrg && (
                <>

                  {/* Aggregate Regional KPIs */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KPICard 
                      label="Participating Employers" 
                      value={regionalHealthStats.totalEmployers} 
                      icon={Building2} 
                      color="text-slate-900" 
                      bg="bg-slate-50" 
                      subtitle={regionalHealthStats.regionName} 
                    />
                    <KPICard 
                      label="Regional Opportunities" 
                      value={regionalHealthStats.totalOpportunities} 
                      icon={Briefcase} 
                      color="text-sky-600" 
                      bg="bg-sky-50" 
                      subtitle={`${regionalHealthStats.totalOpenings} Total Slots`} 
                    />
                    <KPICard 
                      label="Regional Placements" 
                      value={regionalHealthStats.totalPlacements} 
                      icon={CheckCircle} 
                      color="text-emerald-600" 
                      bg="bg-emerald-50" 
                      subtitle={`$${regionalHealthStats.regionalEconomicImpact.toLocaleString()} Value`} 
                    />
                    <KPICard 
                      label="Regional High Schools" 
                      value={regionalHealthStats.regionSchoolsCount} 
                      icon={GraduationCap} 
                      color="text-purple-600" 
                      bg="bg-purple-50" 
                      subtitle={`${regionalHealthStats.activeClustersCount} Career Sectors`} 
                    />
                  </div>

                  {/* Aggregate Sector & Modality Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ReportCard 
                      title="Regional Opportunities by Career Sector" 
                      subtitle={`Anonymized industry distribution across ${regionalHealthStats.regionName}`} 
                      icon={Briefcase}
                    >
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={regionalHealthStats.sectorBreakdown.length > 0 ? regionalHealthStats.sectorBreakdown : [{ name: 'No Postings', value: 0 }]}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: '#64748b' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#64748b' }} />
                            <Tooltip contentStyle={{ fontSize: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                            <Bar dataKey="value" fill="#0094c8" radius={[4, 4, 0, 0]} barSize={24} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </ReportCard>

                    <ReportCard 
                      title="Regional WBL Modality Mix" 
                      subtitle="Distribution of work-based learning experience types in the region" 
                      icon={Globe}
                    >
                      <div className="h-[200px] relative">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie 
                              data={regionalHealthStats.modalityBreakdown.length > 0 ? regionalHealthStats.modalityBreakdown : [{ name: 'No Postings', value: 1 }]} 
                              innerRadius={45} 
                              outerRadius={70} 
                              paddingAngle={4} 
                              dataKey="value"
                            >
                              {(regionalHealthStats.modalityBreakdown.length > 0 ? regionalHealthStats.modalityBreakdown : [{ name: 'No Postings', value: 1 }]).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ fontSize: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <span className="text-xl font-black text-slate-900">{regionalHealthStats.totalOpportunities}</span>
                          <span className="text-[7px] font-black uppercase text-slate-400 font-mono">REGIONAL OPPS</span>
                        </div>
                      </div>
                    </ReportCard>
                  </div>

                  {/* Organization's Regional Contribution Benchmark (Privacy-Safe) */}
                  <ReportCard 
                    title={`Your Regional Ecosystem Contribution (${orgDisplayName})`} 
                    subtitle={`How your organization contributes to overall workforce readiness in ${regionalHealthStats.regionName}`} 
                    icon={TrendingUp}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
                      {[
                        {
                          label: 'Share of Regional Opportunities',
                          yours: stats.totalOpps,
                          regional: regionalHealthStats.totalOpportunities,
                          unit: 'Postings'
                        },
                        {
                          label: 'Share of Regional Position Slots',
                          yours: stats.totalOpenings,
                          regional: regionalHealthStats.totalOpenings,
                          unit: 'Openings'
                        },
                        {
                          label: 'Share of Regional Placements',
                          yours: stats.totalPlacements,
                          regional: regionalHealthStats.totalPlacements,
                          unit: 'Placements'
                        }
                      ].map((metric, idx) => {
                        const pct = metric.regional > 0 ? Math.min(100, Math.round((metric.yours / metric.regional) * 100)) : 0;
                        return (
                          <div key={idx} className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/70 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">{metric.label}</span>
                              <span className="text-xs font-black text-[#0094c8]">{pct}%</span>
                            </div>
                            <div className="h-2 w-full bg-slate-200/70 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#0094c8] rounded-full transition-all duration-500" 
                                style={{ width: `${Math.max(pct, metric.yours > 0 ? 6 : 0)}%` }} 
                              />
                            </div>
                            <div className="flex justify-between text-[9.5px] font-bold text-slate-600">
                              <span>Your Org: {metric.yours} {metric.unit}</span>
                              <span>Region Total: {metric.regional} {metric.unit}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ReportCard>
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
